import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CollaboratorsHistoryProfessionalDataService from "../services/api/collaborators/CollaboratorsHistoryProfessionalData.service";
import CollaboratorsHistoryProfessionalExperiencesService from "../services/api/collaborators/CollaboratorsHistoryProfessionalExperiences.service";
import {
  COLLABORATORS_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import {
  SubmitCollaboratorsHistoryProfessionalExperienceRequest,
  CollaboratorHistoryProfessionalExperience,
} from "../types/api/collaborators.type";
import { useEffect } from "react";
import { useNotification } from "./notification.hook";
import {
  DATA_ADDED_SUCCESS_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../constants/Dictionary";

export const useProfessionalPortrait = (profileId: number) => {
  const { notification } = useNotification();
  const { data, isFetching, isError } = useQuery({
    queryKey: [`get-collaborator-history-professional-data-${profileId}`],
    queryFn: ({ signal }) =>
      CollaboratorsHistoryProfessionalDataService.get(profileId, signal),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isFetching };
};

interface useHistoryExperienceProps {
  profileId: number;
  pageNumber: number;
  searchDebouncedValue: string;
}

export const useHistoryExperience = ({
  profileId,
  pageNumber,
  searchDebouncedValue,
}: useHistoryExperienceProps) => {
  const { notification } = useNotification();
  const { data, isFetching, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-professional-experience-${profileId}`,
      pageNumber,
      searchDebouncedValue,
    ],
    queryFn: ({ signal }) =>
      CollaboratorsHistoryProfessionalExperiencesService.list(
        profileId,
        COLLABORATORS_PAGE_SIZE,
        pageNumber,
        [
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isFetching };
};

export const useDeleteHistoryExperience = (profileId: number) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (selectedExperienceId: number) => {
      return CollaboratorsHistoryProfessionalExperiencesService.delete(
        profileId,
        selectedExperienceId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          `get-collaborator-history-professional-experience-${profileId}`,
        ],
      });
    },
  });

  return { mutateAsync, isPending };
};

export const useSubmitHistoryExperience = (profileId: number) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      body,
      experience,
    }: {
      body: SubmitCollaboratorsHistoryProfessionalExperienceRequest;
      experience?: CollaboratorHistoryProfessionalExperience | null;
    }) => {
      if (!experience) {
        return CollaboratorsHistoryProfessionalExperiencesService.add(
          profileId,
          body,
        );
      } else {
        return CollaboratorsHistoryProfessionalExperiencesService.update(
          profileId,
          experience.id,
          body,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          `get-collaborator-history-professional-experience-${profileId}`,
        ],
      });
    },
  });

  return { mutateAsync, isPending };
};

interface FormData {
  url?: string;
}

export const useLinkedinUrlUpdate = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      data,
      professionalDataId,
    }: {
      data: FormData;
      professionalDataId: number;
    }) => {
      return CollaboratorsHistoryProfessionalDataService.update(
        profileId,
        professionalDataId,
        {
          linkedInUrl: data.url || "",
        },
      );
    },
    onSuccess: () => {
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-professional-data-${profileId}`],
      });
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useLinkedinUrlAdd = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (data: FormData) => {
      return CollaboratorsHistoryProfessionalDataService.add(profileId, {
        linkedInUrl: data.url || "",
      });
    },
    onSuccess: () => {
      notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-professional-data-${profileId}`],
      });
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};
